/**
 * Width-derived default for `runnitState.selectedColumns`.
 *
 * `selectedColumns` is a Vuetify column SPAN, not a column count: the grid is 12 wide, so a
 * span of 3 renders 4 columns. Every value returned here must be one of the spans offered by
 * `SELECTED_COLUMN_MENU_ITEMS` (12, 6, 4, 3, 2, 1), otherwise the settings menu shows no
 * selected item.
 *
 * This is only ever a DEFAULT. An explicit user selection is persisted to localStorage and
 * takes precedence: see `RunnitPageSettings`.
 */

/** Content width (px) at or above which each span applies, widest bucket first. */
const SPAN_BREAKPOINTS: { minWidth: number; span: number }[] = [
	{ minWidth: 1700, span: 2 }, // 6 columns
	{ minWidth: 1100, span: 3 }, // 4 columns
	{ minWidth: 600, span: 4 }, // 3 columns
	{ minWidth: 1, span: 6 }, // 2 columns; matches the hard 2-column floor below `sm`
];

/** Span for 4 columns, used when the measured width is unusable. Matches the historic default. */
const FALLBACK_SPAN: number = 3;

/**
 * The span for a given content width.
 *
 * The input is the width actually available to the gallery, NOT the raw viewport: see
 * `RunnitPageSettings.vue`, which subtracts every app drawer Vuetify has reserved space for
 * (the Runnits side nav, the settings drawer, the gallery drawer) before calling this.
 */
export const deriveSelectedColumnsSpan = (contentWidth: number): number => {
	if (!Number.isFinite(contentWidth) || contentWidth < 1) return FALLBACK_SPAN;
	const bucket = SPAN_BREAKPOINTS.find((b: { minWidth: number; span: number }) => contentWidth >= b.minWidth);
	return bucket ? bucket.span : FALLBACK_SPAN;
};
